import React, { FC, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { useParams } from "react-router-dom";

import { Translate } from "@/app/application/Translate";
import { buildEndpointWithQueryParameters } from "@/data-adapter/SATOSPortal";
import { TrainingKpiStatistic } from "@/features/training/model/Training/TrainingKpiStatistic";
import { TableContainer } from "@/shared/ui";
import {
	Box,
	Checkbox,
	Chip,
	LinearProgress,
	Stack,
	Typography,
	alpha,
	useTheme,
} from "@mui/material";
import {
	DataGrid,
	GridColDef,
	GridRowParams,
	GridToolbar,
} from "@mui/x-data-grid";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

import { useFetchKpiStatistics } from "@/service/TrainingQueries";

dayjs.extend(utc);

interface KpiStatisticsDataGridProps {
	startDate?: Date;
	endDate?: Date;
}

interface AggregatedKpiRow {
	id: number;
	Id: number;
	Name: string;
	Average: number;
	Count: number;
	Required: number;
}

export const KpiStatisticsDataGrid: FC<KpiStatisticsDataGridProps> = (
	props,
) => {
	const navigate = useNavigate();
	const { kpiParameterId } = useParams();
	const theme = useTheme();

	const [paginationModel, setPaginationModel] = useState({
		pageSize: 10,
		page: 0,
	});

	const queryOptions = {
		StartDate: props.startDate
			? dayjs(props.startDate).format("YYYY.MM.DD")
			: undefined,
		EndDate: props.endDate
			? dayjs(props.endDate).format("YYYY.MM.DD")
			: undefined,
	};
	const detailQueryOptions = {
		StartDate: props.startDate
			? dayjs(props.startDate).format("YYYY-MM-DD")
			: undefined,
		EndDate: props.endDate
			? dayjs(props.endDate).format("YYYY-MM-DD")
			: undefined,
	};

	const { isLoading, data: kpiStatistics } =
		useFetchKpiStatistics(queryOptions);
	const kpiRows = useMemo(
		() => (Array.isArray(kpiStatistics) ? kpiStatistics : []),
		[kpiStatistics],
	);
	const selectedKpiId = kpiParameterId ? parseInt(kpiParameterId, 10) : null;
	const periodLabel = `${props.startDate ? dayjs(props.startDate).format("DD/MM/YY") : "—"} → ${
		props.endDate ? dayjs(props.endDate).format("DD/MM/YY") : "—"
	}`;

	const aggregatedKpiRows = useMemo<AggregatedKpiRow[]>(() => {
		const byId = new Map<
			number,
			TrainingKpiStatistic & { weightedSum: number }
		>();

		for (const item of kpiRows) {
			if (typeof item?.Id !== "number") continue;
			const safeName = typeof item.Name === "string" ? item.Name : "";
			const safeAverage = typeof item.Average === "number" ? item.Average : 0;
			const safeCount = typeof item.Count === "number" ? item.Count : 0;
			const safeRequired =
				typeof item.Required === "number" ? item.Required : 0;
			const existing = byId.get(item.Id);
			if (!existing) {
				byId.set(item.Id, {
					...item,
					Name: safeName,
					Average: safeAverage,
					Count: safeCount,
					Required: safeRequired,
					weightedSum: safeAverage * safeCount,
				});
				continue;
			}

			existing.Count += safeCount;
			existing.weightedSum += safeAverage * safeCount;
			existing.Average =
				existing.Count > 0 ? existing.weightedSum / existing.Count : 0;
			existing.Required = safeRequired || existing.Required;
		}

		return Array.from(byId.values())
			.map((row) => ({
				id: row.Id,
				Id: row.Id,
				Name: row.Name,
				Average: row.Average,
				Count: row.Count,
				Required: row.Required,
			}))
			.sort((a, b) => String(a.Name ?? "").localeCompare(String(b.Name ?? "")));
	}, [kpiRows]);
	const totalSamples = aggregatedKpiRows.reduce((sum, row) => sum + row.Count, 0);
	const avgAcrossKpis =
		aggregatedKpiRows.length > 0
			? aggregatedKpiRows.reduce((sum, row) => sum + row.Average, 0) /
				aggregatedKpiRows.length
			: null;

	const columns: GridColDef[] = [
		{
			field: "Name",
			renderHeader: () => (
				<Typography component="span" sx={{ fontWeight: 700 }}>
					<Translate String="Training.Archive.KpiName">Name</Translate>
				</Typography>
			),
			flex: 1.4,
			minWidth: 140,
			sortable: true,
		},
		{
			field: "Count",
			renderHeader: () => (
				<Typography component="span" sx={{ fontWeight: 700 }}>
					<Translate String="Training.Archive.KpiCount">Count</Translate>
				</Typography>
			),
			flex: 0.6,
			minWidth: 90,
			sortable: true,
			align: "right",
			headerAlign: "right",
		},
		{
			field: "Required",
			renderHeader: () => (
				<Typography component="span" sx={{ fontWeight: 700 }}>
					<Translate String="Training.Archive.KpiRequired">Required</Translate>
				</Typography>
			),
			flex: 0.7,
			minWidth: 100,
			sortable: true,
			align: "right",
			headerAlign: "right",
		},
	];

	if (isLoading) {
		return (
			<Box
				sx={{
					display: "flex",
					flexDirection: "column",
					alignItems: "center",
					justifyContent: "center",
					minHeight: 240,
					gap: 2,
				}}
			>
				<LinearProgress sx={{ width: "100%" }} />
				<Typography variant="body2" color="text.secondary">
					<Translate String="Training.Archive.LoadingKpiStatistics">
						Loading KPI statistics…
					</Translate>
				</Typography>
			</Box>
		);
	}

	if (!aggregatedKpiRows || aggregatedKpiRows.length === 0) {
		return (
			<Box
				sx={{
					p: 4,
					borderRadius: 3,
					textAlign: "center",
					border: "1px dashed",
					borderColor: "divider",
					bgcolor: (t) =>
						alpha(
							t.palette.primary.main,
							t.palette.mode === "dark" ? 0.04 : 0.02,
						),
				}}
			>
				<Typography variant="body1" color="text.secondary" fontWeight={600}>
					<Translate String="Training.Archive.NoneFound">None found</Translate>
				</Typography>
				<Typography variant="body2" color="text.disabled" sx={{ mt: 0.5 }}>
					{periodLabel}
				</Typography>
			</Box>
		);
	}

	const handleRowClick = (params: GridRowParams<AggregatedKpiRow>) => {
		const to = buildEndpointWithQueryParameters(
			`/training/archive/kpiParameters/${params.id}`,
			detailQueryOptions,
		);
		navigate(to);
	};

	const dataGridSx = {
		border: "none",
		bgcolor: "transparent",
		color: "text.primary",
		"& .MuiDataGrid-root": { border: "none" },
		"& .MuiDataGrid-columnHeaders": {
			bgcolor:
				theme.palette.mode === "light"
					? alpha(theme.palette.primary.main, 0.04)
					: alpha(theme.palette.common.white, 0.03),
			borderBottom: `1px solid ${theme.palette.divider}`,
			minHeight: "48px !important",
			"& .MuiDataGrid-columnHeaderTitle": {
				fontWeight: 700,
				fontSize: theme.typography.body2.fontSize,
			},
		},
		"& .MuiDataGrid-cell": {
			borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
		},
		"& .MuiDataGrid-row": {
			bgcolor: "background.paper",
		},
		"& .MuiDataGrid-row:hover": {
			bgcolor: alpha(
				theme.palette.primary.main,
				theme.palette.mode === "light" ? 0.06 : 0.12,
			),
			cursor: "pointer",
		},
		"& .MuiDataGrid-row.kpi-row-selected": {
			bgcolor: alpha(
				theme.palette.primary.main,
				theme.palette.mode === "light" ? 0.12 : 0.24,
			),
		},
		"& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": {
			outline: "none",
		},
		"& .MuiDataGrid-cell:focus-visible": {
			boxShadow: `inset 0 0 0 2px ${theme.palette.primary.main}`,
		},
		"& .MuiDataGrid-footerContainer": {
			borderTop: `1px solid ${theme.palette.divider}`,
			bgcolor: theme.palette.mode === "dark" ? alpha(theme.palette.common.white, 0.02) : "grey.50",
		},
		"& .MuiDataGrid-toolbarContainer": {
			borderBottom: `1px solid ${theme.palette.divider}`,
			bgcolor: theme.palette.mode === "dark" ? alpha(theme.palette.common.white, 0.02) : "grey.50",
			px: 1,
			py: 0.75,
			gap: 1,
		},
		"& .MuiInputBase-root .MuiOutlinedInput-notchedOutline": {
			borderColor: theme.palette.divider,
		},
		"& .MuiDataGrid-toolbarContainer .MuiButton-root": {
			borderRadius: 2,
			textTransform: "none",
		},
		"& .MuiDataGrid-toolbarContainer .MuiInputBase-root": {
			borderRadius: 2,
			bgcolor: "background.paper",
		},
	};

	return (
		<Box
			sx={{
				borderRadius: 3,
				overflow: "hidden",
				border: 1,
				borderColor: "divider",
				bgcolor: "background.paper",
			}}
		>
			<Box
				sx={(t) => ({
					px: 2.25,
					py: 1,
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					gap: 1.25,
					flexWrap: "wrap",
					borderBottom: 1,
					borderColor: alpha(t.palette.divider, 0.85),
					bgcolor: t.palette.mode === "dark" ? alpha(t.palette.common.white, 0.02) : "grey.50",
				})}
			>
				<Box sx={{ minWidth: 0, flex: 1 }}>
					<Typography
						variant="caption"
						sx={{
							display: "block",
							mb: 0.05,
							color: "primary.main",
							fontWeight: 700,
							letterSpacing: "0.08em",
							textTransform: "uppercase",
						}}
					>
						<Translate String="Auto.Features.Training.Pages.Archive.PanelLeft.KpiStatisticsDataGrid.L355C7.Analytics">Analytics</Translate>
					</Typography>
					<Typography variant="subtitle1" fontWeight={700} sx={{ lineHeight: 1.05 }}>
						<Translate String="Training.Archive.KpiStatistics">
							KPI Statistics
						</Translate>
					</Typography>
					<Typography variant="body2" color="text.secondary">
						{periodLabel}
					</Typography>
				</Box>
				<Stack
					direction="row"
					spacing={0.75}
					sx={{
						maxWidth: "100%",
						overflowX: "auto",
						whiteSpace: "nowrap",
						flexWrap: "nowrap",
						pb: 0.25,
						"&::-webkit-scrollbar": { height: 6 },
						"&::-webkit-scrollbar-thumb": {
							backgroundColor: alpha(theme.palette.text.primary, 0.18),
							borderRadius: 999,
						},
					}}
				>
					<Chip
						size="small"
						label={
							<Stack direction="row" spacing={0.75} alignItems="center">
								<Typography variant="caption" color="text.secondary" fontWeight={800}>
									<Translate String="Auto.Features.Training.Pages.Archive.PanelLeft.KpiStatisticsDataGrid.L387C79.KPIs">KPIs</Translate>
								</Typography>
								<Typography variant="caption" fontWeight={950}>
									{aggregatedKpiRows.length}
								</Typography>
							</Stack>
						}
						sx={{
							height: 26,
							borderRadius: 2,
							bgcolor: alpha(
								theme.palette.primary.main,
								theme.palette.mode === "dark" ? 0.16 : 0.09,
							),
							"& .MuiChip-label": { px: 1 },
						}}
					/>
					<Chip
						size="small"
						label={
							<Stack direction="row" spacing={0.75} alignItems="center">
								<Typography variant="caption" color="text.secondary" fontWeight={800}>
									<Translate String="Auto.Features.Training.Pages.Archive.PanelLeft.KpiStatisticsDataGrid.L409C79.Samples">Samples</Translate>
								</Typography>
								<Typography variant="caption" fontWeight={950}>
									{totalSamples.toLocaleString()}
								</Typography>
							</Stack>
						}
						sx={{
							height: 26,
							borderRadius: 2,
							bgcolor: alpha(
								theme.palette.info.main,
								theme.palette.mode === "dark" ? 0.16 : 0.09,
							),
							"& .MuiChip-label": { px: 1 },
						}}
					/>
				</Stack>
			</Box>

			<TableContainer height={420} sx={{ border: "none", bgcolor: "background.paper" }}>
				<DataGrid
					rows={aggregatedKpiRows}
					columns={columns}
					rowHeight={44}
					onRowClick={handleRowClick}
					paginationModel={paginationModel}
					onPaginationModelChange={setPaginationModel}
					getRowClassName={(params) =>
						selectedKpiId != null && params.id === selectedKpiId
							? "kpi-row-selected"
							: ""
					}
					disableColumnSelector
					disableDensitySelector
					disableColumnFilter
					slots={{
						toolbar: GridToolbar,
						booleanCellTrueIcon: () => <Checkbox checked />,
						booleanCellFalseIcon: () => <Checkbox />,
					}}
					slotProps={{
						toolbar: {
							showQuickFilter: true,
							quickFilterProps: { debounceMs: 300 },
							printOptions: { disableToolbarButton: true },
							csvOptions: { disableToolbarButton: true },
						},
					}}
					sx={dataGridSx}
				/>
			</TableContainer>
		</Box>
	);
};

export default KpiStatisticsDataGrid;
